import { FC, useEffect, useRef } from "react";
import { getCSS, getCSSVariableValue } from "../../../../assets/ts/_utils";
import { useThemeMode } from "../../../layout/theme-mode/ThemeModeProvider";
import ApexCharts from "apexcharts";

export interface TransactionStatsData {
    fiat_count: number;
    fiat_amount: number;
    crypto_count: number;
    total_withdrawal_amount: number;
}

interface CardsDashboardTransactionsProps {
    className?: string;
    data?: {
        transaction_stats?: TransactionStatsData;
    };
}

const CardsDashboardTransactions: FC<CardsDashboardTransactionsProps> = ({
    className = "",
    data,
}) => {
    const chartRef = useRef<HTMLDivElement | null>(null);
    const { mode } = useThemeMode();

    useEffect(() => {
        let chart;
        if (data?.transaction_stats) {
            chart = refreshChart();
        }

        return () => {
            if (chart) {
                chart.destroy();
            }
        };
    }, [chartRef, mode, data]);

    const refreshChart = () => {
        if (!chartRef.current || !data?.transaction_stats) {
            return;
        }

        const height = parseInt(getCSS(chartRef.current, "height")) + 10;

        const chart = new ApexCharts(
            chartRef.current,
            getChartOptions(height, {
                Fiat: data.transaction_stats.fiat_count,
                Crypto: data.transaction_stats.crypto_count,
            })
        );
        if (chart) {
            chart.render();
        }

        return chart;
    };

    const fiatCount = data?.transaction_stats?.fiat_count ?? 0;
    const fiatAmount = data?.transaction_stats?.fiat_amount ?? 0;
    const cryptoCount = data?.transaction_stats?.crypto_count ?? 0;
    const totalWithdrawalAmount = data?.transaction_stats?.total_withdrawal_amount ?? 0;

    return (
        <div className={`card card-flush h-lg-100 ${className}`}>
            {/* Header */}
            <div className="card-header pt-5">
                <h3 className="card-title align-items-start flex-column">
                    <span className="card-label fw-bold text-gray-900">
                        Transactions Overview
                    </span>
                    <span className="text-gray-500 mt-1 fw-semibold fs-6">
                        Summary of recent financial activity
                    </span>
                </h3>
                <div className="card-toolbar">
                    <span className="badge badge-light-success fs-base mt-n3">
                        <i className="ki-duotone ki-arrow-up fs-5 text-success ms-n1">
                            <span className="path1" />
                            <span className="path2" />
                        </i>
                        Updated
                    </span>
                </div>
            </div>

            {/* Body */}
            <div className="card-body d-flex align-items-end pt-6">
                <div className="row align-items-center mx-0 w-100">
                    {/* Labels Column */}
                    <div className="col-7 px-0">
                        <div className="d-flex flex-column content-justify-center">
                            {/* Fiat Transactions */}
                            <div className="d-flex fs-6 fw-semibold align-items-center my-2">
                                <div
                                    className="bullet bg-success me-3"
                                    style={{ borderRadius: 3, width: 12, height: 12 }}
                                />
                                <div className="fs-5 fw-bold text-gray-600 me-5">Fiat Transactions:</div>
                                <div className="ms-auto fw-bolder text-gray-700 text-end">
                                    {fiatCount}
                                    {/* — ${fiatAmount.toLocaleString()} */}
                                </div>
                            </div>

                            {/* Crypto Transactions */}
                            <div className="d-flex fs-6 fw-semibold align-items-center my-2">
                                <div
                                    className="bullet bg-primary me-3"
                                    style={{ borderRadius: 3, width: 12, height: 12 }}
                                />
                                <div className="fs-5 fw-bold text-gray-600 me-5">Crypto Transactions:</div>
                                <div className="ms-auto fw-bolder text-gray-700 text-end">
                                    {cryptoCount}
                                </div>
                            </div>

                            {/* ACH Transactions */}
                            {/* <div className="d-flex fs-6 fw-semibold align-items-center my-2">
                                <div
                                    className="bullet bg-info me-3"
                                    style={{ borderRadius: 3, width: 12, height: 12 }}
                                />
                                <div className="fs-5 fw-bold text-gray-600 me-5">ACH Transactions:</div>
                                <div className="ms-auto fw-bolder text-gray-700 text-end">
                                    {achCount} — ${achAmount.toLocaleString()}
                                </div>
                            </div> */}

                            {/* Total Withdrawals */}
                            <div className="d-flex fs-6 fw-semibold align-items-center mt-4 pt-2 border-top">
                                <div
                                    className="bullet me-3"
                                    style={{
                                        borderRadius: 3,
                                        backgroundColor: "#E4E6EF",
                                        width: 12,
                                        height: 12,
                                    }}
                                />
                                <div className="fs-5 fw-bold text-gray-600 me-5">Total Withdrawals:</div>
                                <div className="ms-auto fw-bolder text-gray-700 text-end">
                                    ${totalWithdrawalAmount.toLocaleString()}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Chart Placeholder */}
                    <div className="col-5 d-flex justify-content-end px-0">
                        <div
                            ref={chartRef}
                            className="mt-2"
                            id="kt_card_widget_17_chart"
                            style={{ height: "200px", width: "200px" }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

function getChartOptions(height: number, data: any) {
    return {
        series: Object.values(data),
        labels: Object.keys(data),
        colors: ["#28a745", "#007bff", "#17a2b8", "#ffc107"],
        chart: {
            height,
            type: "donut",
        },
        dataLabels: {
            enabled: false,
        },
        responsive: [
            {
                breakpoint: 480,
                options: {
                    chart: {
                        height,
                        width: height,
                    },
                    legend: {
                        show: false,
                    },
                },
            },
        ],
        legend: {
            show: false,
        },
    };
}

export { CardsDashboardTransactions };
